import { cn } from "../../../lib/utils";

export type TimeOption = {
	value: number;
	label: string;
};

export const TIME_OPTIONS: TimeOption[] = [
	{ value: 0, label: "Midnight" },
	{ value: 9, label: "9:00 am" },
	{ value: 12, label: "Noon" },
	{ value: 17, label: "5:00 pm" },
];

export const CALENDAR_STYLES = {
	base: {
		months: "flex flex-col",
		monthsMultiple: "flex flex-row gap-4",
		month: "space-y-2 mb-2",
		nav: "flex items-center absolute left-0 right-0 justify-between px-2",
		weekday: "text-neutral-5 font-normal text-[12px] h-[32px] w-[40px] text-center leading-[32px]",
		today: "border-none",
		outside: "[&>button]:text-neutral-5",
		disabled: "[&>button]:text-neutral-5 [&>button]:cursor-not-allowed [&>button]:hover:!bg-neutral-0",
		dropdowns: "flex flex-row gap-1",
	},
	caption: {
		withDropdowns: "flex justify-center relative items-center gap-1 [&_label]:hidden [&_.rdp-caption_label]:hidden",
		withoutDropdowns: "h-8 flex justify-center items-center",
		label: "text-paragraph-3 font-semibold",
		labelHidden: "hidden",
	},
	navigation: {
		button: "size-8 border-none bg-transparent p-0 opacity-50 hover:opacity-100 flex items-center justify-center cursor-pointer z-10",
	},
	day: {
		base: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20",
		selected:
			"[&.rdp-day_selected]:!bg-neutral-9 [&.rdp-day_selected]:!text-neutral-0 [&.rdp-day_selected]:hover:!bg-neutral-9",
		rangeStart:
			"[&.rdp-range_start]:!bg-neutral-9 [&.rdp-range_start]:!text-neutral-0 [&.rdp-range_start]:!rounded-l-[2px] [&.rdp-range_start]:hover:!bg-neutral-9",
		rangeEnd:
			"[&.rdp-range_end]:!bg-neutral-9 [&.rdp-range_end]:!text-neutral-0 [&.rdp-range_end]:!rounded-r-[2px] [&.rdp-range_end]:hover:!bg-neutral-9",
		rangeMiddle:
			"[&.rdp-range_middle]:!bg-neutral-1 [&.rdp-range_middle]:!text-neutral-9 [&.rdp-range_middle]:!rounded-none [&.rdp-range_middle]:hover:!bg-neutral-1",
	},
	dayButton: {
		base: "h-8 w-10 p-0 font-normal cursor-pointer border-none bg-neutral-0 rounded-[2px] transition-colors",
		hover: "hover:bg-neutral-1",
		selected: "aria-selected:!bg-neutral-9 aria-selected:!text-neutral-0 aria-selected:hover:!bg-neutral-9",
	},
	range: {
		middle: "!bg-neutral-1 !text-neutral-9 !rounded-none [&>button]:!bg-neutral-1 [&>button]:!text-neutral-9 [&>button]:!rounded-none hover:!bg-neutral-1 [&>button]:hover:!bg-neutral-1",
		start: "!bg-neutral-9 !text-neutral-0 !rounded-l-[2px] [&>button]:!bg-neutral-9 [&>button]:!text-neutral-0 [&>button]:!rounded-l-[2px] hover:!bg-neutral-9 [&>button]:hover:!bg-neutral-9",
		end: "!bg-neutral-9 !text-neutral-0 !rounded-r-[2px] [&>button]:!bg-neutral-9 [&>button]:!text-neutral-0 [&>button]:!rounded-r-[2px] hover:!bg-neutral-9 [&>button]:hover:!bg-neutral-9",
		selected:
			"bg-neutral-9 text-neutral-0 rounded-[2px] [&>button]:bg-neutral-9 [&>button]:text-neutral-0 hover:!bg-neutral-9 [&>button]:hover:!bg-neutral-9",
	},
} as const;

export const DROPDOWN_CONFIG = {
	years: {
		width: "w-20",
		className:
			"[&_input]:text-paragraph-3 [&_input]:font-semibold [&_[data-radix-select-content]]:shadow-none [&_[data-radix-popover-content]]:shadow-none [&_[data-side]]:shadow-none",
	},
	months: {
		width: "w-[120px]",
		className:
			"[&_input]:text-paragraph-3 [&_input]:font-semibold [&_[data-radix-select-content]]:shadow-none [&_[data-radix-popover-content]]:shadow-none [&_[data-side]]:shadow-none",
	},
	time: {
		width: "w-[100px]",
		className:
			"[&_input]:text-sm [&_[data-radix-select-content]]:shadow-none [&_[data-radix-popover-content]]:shadow-none",
	},
} as const;

export const CALENDAR_CONFIG = {
	defaultWidth: "w-[280px]",
	multipleMonthsWidth: "w-auto",
	baseClassName:
		"rounded-md border border-solid border-neutral-3 p-2 [&_.rdp-caption>*:not(:last-child)]:mr-1 [&_.rdp-dropdown:not(:last-child)]:mr-1 [&_select:not(:last-child)]:mr-1",
	containerClassName: "relative overflow-hidden [div:has(&)]:shadow-none",
	timeDropdownPadding: "pb-0",
} as const;

/**
 * Generates the classNames object for react-day-picker's internal elements
 * This maps specific class names to each part of the calendar (days, buttons, navigation, etc.)
 */
export const createDayPickerClassNames = (showMonthAndYearDropdowns: boolean, numberOfMonths?: number) => ({
	months: numberOfMonths && numberOfMonths > 1 ? CALENDAR_STYLES.base.monthsMultiple : CALENDAR_STYLES.base.months,
	month: CALENDAR_STYLES.base.month,
	month_caption: showMonthAndYearDropdowns
		? CALENDAR_STYLES.caption.withDropdowns
		: CALENDAR_STYLES.caption.withoutDropdowns,
	caption_label: showMonthAndYearDropdowns ? CALENDAR_STYLES.caption.labelHidden : CALENDAR_STYLES.caption.label,
	nav: CALENDAR_STYLES.base.nav,
	button_previous: cn(CALENDAR_STYLES.navigation.button),
	button_next: cn(CALENDAR_STYLES.navigation.button),
	weekday: CALENDAR_STYLES.base.weekday,
	day: cn(
		CALENDAR_STYLES.day.base,
		CALENDAR_STYLES.day.selected,
		CALENDAR_STYLES.day.rangeStart,
		CALENDAR_STYLES.day.rangeEnd,
		CALENDAR_STYLES.day.rangeMiddle
	),
	today: CALENDAR_STYLES.base.today,
	day_button: cn(
		CALENDAR_STYLES.dayButton.base,
		CALENDAR_STYLES.dayButton.hover,
		"rdp-day_button",
		CALENDAR_STYLES.dayButton.selected
	),
	range_middle: CALENDAR_STYLES.range.middle,
	range_start: CALENDAR_STYLES.range.start,
	range_end: CALENDAR_STYLES.range.end,
	selected: CALENDAR_STYLES.range.selected,
	outside: CALENDAR_STYLES.base.outside,
	disabled: CALENDAR_STYLES.base.disabled,
	dropdowns: CALENDAR_STYLES.base.dropdowns,
});

/**
 * Generates the root container className string for the Calendar component
 * This combines width, base styling, container behavior, and optional custom classes
 */
export const createCalendarRootClassName = (showTimeDropdown: boolean, numberOfMonths?: number, className?: string) =>
	cn(
		numberOfMonths && numberOfMonths > 1 ? CALENDAR_CONFIG.multipleMonthsWidth : CALENDAR_CONFIG.defaultWidth,
		CALENDAR_CONFIG.baseClassName,
		CALENDAR_CONFIG.containerClassName,
		showTimeDropdown && CALENDAR_CONFIG.timeDropdownPadding,
		className
	);
